import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import { CountUp, Decode, Spark } from '../lib/fx';

interface StatusResponse {
  warnings: string[];
  workspaceRoot: string;
  tz: string;
  env: string;
}
interface AuthStatus {
  loggedIn: boolean;
  authMethod?: string;
  subscriptionType?: string;
  email?: string;
  error?: string;
}
interface UsageBucket {
  pct: number;
  resets: string;
}
interface Usage {
  raw: string;
  session?: UsageBucket;
  week?: UsageBucket;
  scoped?: (UsageBucket & { name: string })[];
  error?: string;
}
// Közös keret: a hivatalos % mellett a saját rész becslése (/api/quota)
interface OurUse { cost: number; input: number; output: number; cache_read: number; cache_write: number; messages: number; files: number }
interface QuotaWin { pct: number; resets_at: string | null; our: OurUse | null; mine_pct: number | null; other_pct: number | null; limit_usd: number | null; windows: number; confidence: 'none' | 'low' | 'ok' }
interface Quota { session: QuotaWin | null; week: QuotaWin | null; samples: number; calibrating_since: number | null; ratio: number | null; updated_at: number | null; stale?: boolean; error?: string | null }
interface ProjectUsage { project_id: number; name: string; dir: string; sessions: number; input_tokens: number; output_tokens: number; cache_read_tokens: number; last_at: number | null }
interface Month { input_tokens: number; output_tokens: number; cache_read_tokens: number; entries: number }
interface SessionInfo {
  id: string;
  name: string;
  cwd: string;
  projectId: number | null;
  kind?: 'main' | 'worker';
}

const panel = 'a-panel a-pad';

interface UpdateInfo { local: boolean; current?: string; running?: string; restartNeeded?: boolean; latest?: string | null; available?: boolean; reachable?: boolean; busy?: boolean }
// Frissítés-panel: csak a helyi (indítóval futó) példányon látszik. Frissítés után a szerver újraindul, az oldal magától újratölt.
function UpdatePanel() {
  const [info, setInfo] = useState<UpdateInfo | null>(null);
  const [busy, setBusy] = useState<'check' | 'update' | 'restart' | null>(null);
  const [msg, setMsg] = useState('');
  const check = async () => { setBusy('check'); try { setInfo(await api<UpdateInfo>('/api/update')); } catch { /* nincs */ } finally { setBusy(null); } };
  useEffect(() => { void check(); }, []);
  const update = async () => {
    setBusy('update'); setMsg('Letöltés és telepítés… (1–2 perc)');
    try {
      const r = await api<{ updated: boolean; version: string }>('/api/update', { method: 'POST' });
      if (!r.updated) { setMsg('Naprakész — nincs mit frissíteni.'); setBusy(null); await check(); return; }
      setMsg(`Frissítve: ${r.version} — újraindul…`); setBusy('restart');
      const t0 = Date.now(); await new Promise((r2) => setTimeout(r2, 2500));
      while (Date.now() - t0 < 90_000) { try { const h = await fetch('/healthz', { cache: 'no-store' }); if (h.ok) { location.reload(); return; } } catch { /* még indul */ } await new Promise((r2) => setTimeout(r2, 1500)); }
      setMsg('Nem jött vissza — indítsd el kézzel az AI Project Managert.'); setBusy(null);
    } catch (e) { setMsg('A frissítés nem sikerült: ' + (e instanceof Error ? e.message : '')); setBusy(null); }
  };
  if (!info || !info.local) return null;
  const badge = info.available
    ? <span className="ml-2 rounded bg-[var(--color-accent)]/20 px-2 py-0.5 text-xs text-[var(--color-accent)]">új kiadás: {info.latest}</span>
    : info.reachable ? <span className="ml-2 text-xs text-neutral-500">naprakész</span> : <span className="ml-2 text-xs text-amber-400">a kiadás-szerver nem érhető el</span>;
  return (
    <div className={`${panel} mb-4 flex flex-wrap items-center justify-between gap-3`}>
      <div>
        <div className="text-sm font-semibold text-neutral-100">Verzió: <span className="font-mono">{info.running ?? info.current}</span>{info.restartNeeded ? <span className="ml-2 rounded bg-amber-500/20 px-2 py-0.5 text-xs text-amber-300">a lemezen már {info.current} van — újraindítás kell</span> : badge}</div>
        {msg && <div className="mt-1 text-xs text-neutral-400">{msg}</div>}
      </div>
      <button onClick={() => void update()} disabled={busy !== null || (!info.reachable && !info.restartNeeded)} className="rounded-lg bg-[var(--color-accent)] px-5 py-2 text-sm font-medium text-black transition hover:brightness-110 disabled:opacity-40">{busy === 'update' ? 'Frissítés…' : busy === 'restart' ? 'Újraindul…' : 'Update'}</button>
    </div>
  );
}


interface Day { date: string; output: number }
interface Call { project_id: number; project: string | null; label: string; detail: string; at: number }
interface LiveSession extends SessionInfo { alive: boolean; lastOutputAt?: number }

const fmt = (n: number) => (n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1).replace('.', ',')} M` : n >= 1000 ? `${Math.round(n / 1000)} k` : String(n));
const agoText = (t: number) => { const m = Math.round((Date.now() - t) / 60000); return m < 1 ? 'most' : m < 60 ? `${m} perce` : `${Math.round(m / 60)} órája`; };

// Limit-csempe: szaggatott mérő, színe a telítettség szerint
function LimitTile({ label, bucket, scoped }: { label: string; bucket?: UsageBucket; scoped?: (UsageBucket & { name: string })[] }) {
  const pct = bucket?.pct ?? 0;
  return (
    <div className="a-kpi">
      <span className="a-kl">{label}</span>
      <span className="a-kv">{bucket ? <CountUp value={pct} suffix="%" /> : '—'}</span>
      {scoped?.map((s) => <span key={s.name} className={`kscope ${s.pct >= 90 ? 'bad' : s.pct >= 70 ? 'warn' : ''}`} title={`Csak a(z) ${s.name} modellre vonatkozó heti keret — újraindul: ${s.resets}`}>{s.name}: {Math.round(s.pct)}%</span>)}
      <span className="kf">{bucket?.resets ? `újraindul: ${bucket.resets}` : 'nincs adat'}</span>
      <span className="a-meter"><i className={`a-grow ${pct >= 90 ? 'bad' : pct >= 70 ? 'warn' : ''}`} style={{ width: `${Math.min(pct, 100)}%` }} /></span>
    </div>
  );
}

const pctTxt = (n: number) => `${Math.round(n)}%`;
const sinceTxt = (t: number | null) => (t ? new Date(t).toLocaleString('hu-HU', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

// „Ebből én”: a közös heti keretből mennyi a saját (a szerver átirataiból), mennyi a másik felhasználóé.
// A keret nagyságát a hivatalos % és a saját fogyasztás együttmozgásából kalibráljuk — kezdetben ezért csak a saját fogyasztás látszik.
function ShareTile({ q }: { q: Quota | null }) {
  const w = q?.week, s = q?.session;
  const ready = w && w.confidence !== 'none' && w.mine_pct != null;
  const mine = ready ? w.mine_pct! : 0, other = ready ? (w.other_pct ?? 0) : 0;
  const title = [
    w?.our ? `Saját fogyasztás a héten (a szerver összes Claude Code átiratából, üzenetenként egyszer számolva): ${fmt(w.our.output)} kimenet-, ${fmt(w.our.input + w.our.cache_write)} bemenet-, ${fmt(w.our.cache_read)} cache-token, ${w.our.messages} válasz. API-áron ≈ ${Math.round(w.our.cost)}.` : '',
    w?.limit_usd ? `A heti keret becsült nagysága: legalább ≈ ${Math.round(w.limit_usd)} API-egyenérték.` : '',
    'A szétválasztás becslés: a keret nagyságát a hivatalos % és a saját fogyasztás együttmozgásából számoljuk (5 percenként minta). Amíg kevés a mérés, a saját részt inkább felülbecsli; a gép más eszközeiről (játék-gép, claude.ai) érkező fogyasztás a „más” részbe kerül.',
    q?.calibrating_since ? `Mérés kezdete: ${sinceTxt(q.calibrating_since)}, ${q.samples} minta.` : '',
  ].filter(Boolean).join('\n');
  return (
    <div className="a-kpi" title={title}>
      <span className="a-kl">Ebből én · hét</span>
      {!w ? <span className="a-kv">—</span> : ready ? (
        <span className="a-kv">{w.confidence === 'low' ? <small className="kapprox">~</small> : null}<CountUp value={Math.round(mine)} suffix="%" /><small>a {pctTxt(w.pct)}-ból</small></span>
      ) : (
        <span className="a-kv kcal">{w.our ? <>{fmt(w.our.output)}<small>kimenet</small></> : '—'}</span>
      )}
      <span className="kf">{!w ? (q?.error === 'old_server' ? 'az APM Restart után indul' : q?.error ? 'a keret most nem kérdezhető le' : 'betöltés…')
        : ready ? <>más: ~{pctTxt(other)}{s && s.mine_pct != null ? <> · munkamenet: én ~{pctTxt(s.mine_pct)} / {pctTxt(s.pct)}</> : null}{w.confidence === 'low' ? <> · kalibrálódik</> : null}</>
        : <>saját token a héten · arány ~1 nap mérés után</>}</span>
      <span className="a-meter dual">{ready ? <><i className="a-grow" style={{ width: `${Math.min(mine, 100)}%` }} /><i className="a-grow other" style={{ width: `${Math.min(other, 100 - Math.min(mine, 100))}%` }} /></> : w ? <i className="a-grow other" style={{ width: `${Math.min(w.pct, 100)}%` }} /> : null}</span>
    </div>
  );
}

export function Dashboard() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<StatusResponse | null>(null);
  const [auth, setAuth] = useState<AuthStatus | null>(null);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [quota, setQuota] = useState<Quota | null>(null);
  const [stats, setStats] = useState<{ projects: ProjectUsage[]; month: Month } | null>(null);
  const [sessions, setSessions] = useState<LiveSession[]>([]);
  const [days, setDays] = useState<Day[]>([]);
  const [calls, setCalls] = useState<(Call & { fresh?: boolean })[]>([]);

  useEffect(() => {
    void api<StatusResponse>('/api/status').then(setStatus).catch(() => {});
    void api<AuthStatus>('/api/auth-status').then(setAuth).catch(() => {});
    void api<Usage>('/api/usage').then(setUsage).catch(() => {});
    void api<Quota>('/api/quota').then(setQuota).catch((e) => setQuota({ session: null, week: null, samples: 0, calibrating_since: null, ratio: null, updated_at: null, error: e instanceof ApiError && e.status === 404 ? 'old_server' : 'unavailable' }));
    void api<{ projects: ProjectUsage[]; month: Month }>('/api/stats').then(setStats).catch(() => {});
    void api<{ days: Day[] }>('/api/n4/daily?days=14').then((r) => setDays(r.days)).catch(() => {});
    void api<{ calls: Call[] }>('/api/n4/calls').then((r) => setCalls(r.calls.slice(0, 8))).catch(() => {});
  }, []);
  // Élő hívások a héjtól (apm-call esemény): felülre csúsznak be
  useEffect(() => {
    const on = (e: Event) => setCalls((p) => [{ ...(e as CustomEvent<Call>).detail, fresh: true }, ...p.map((c) => ({ ...c, fresh: false }))].slice(0, 8));
    window.addEventListener('apm-call', on); return () => window.removeEventListener('apm-call', on);
  }, []);
  useEffect(() => {
    const load = () => api<{ sessions: LiveSession[] }>('/api/sessions').then((r) => setSessions(r.sessions)).catch(() => setSessions([]));
    void load(); const t = window.setInterval(() => void load(), 5000); return () => clearInterval(t);
  }, []);

  const live = sessions.filter((s) => s.alive && s.kind !== 'worker');
  const workers = sessions.filter((s) => s.alive && s.kind === 'worker').length;
  const today = days.length ? days[days.length - 1]!.output : 0;
  const maxDay = Math.max(1, ...days.map((d) => d.output));
  const projs = (stats?.projects ?? []).filter((p) => p.output_tokens > 0).sort((a, b) => b.output_tokens - a.output_tokens);
  const maxOut = Math.max(1, ...projs.map((p) => p.output_tokens));

  return (
    <div className="h-full overflow-auto">
      <div className="a-pg">
        {status?.warnings.map((w, i) => <div key={i} className="a-warnbar">⚠ {w}</div>)}
        <UpdatePanel />
        <header className="a-pgh">
          <div><h1 className="a-title"><Decode text="Dashboard" duration={800} /></h1>
            <p className="a-sub">Ma eddig <b>{fmt(today)}</b> kimenet-token, {live.length} élő ügynök-ablak{workers ? `, ${workers} munkás` : ''}. {auth && (auth.loggedIn ? <span>Claude: bejelentkezve{auth.subscriptionType ? ` (${auth.subscriptionType})` : ''}.</span> : <span style={{ color: '#ff9c9c' }}>A Claude CLI nincs bejelentkezve: léptesd be újra a szerveren.</span>)}</p></div>
        </header>
        <section className="a-kpis a-stagger">
          <div className="a-kpi" style={{ ['--i' as string]: 0 }}>
            <span className="a-kl">Kimenet ma</span>
            <span className="a-kv">{today >= 1_000_000 ? <><CountUp value={today / 1_000_000} decimals={1} /><small>M token</small></> : <><CountUp value={Math.round(today / 1000)} /><small>k token</small></>}</span>
            <span className="kf">30 nap: {stats ? fmt(stats.month.output_tokens) : '—'}</span>
            <Spark values={days.slice(-7).map((d) => d.output)} className="kspark" />
          </div>
          <div style={{ ['--i' as string]: 1 }}><LimitTile label="Munkamenet-limit" bucket={usage?.session} /></div>
          <div style={{ ['--i' as string]: 2 }}><LimitTile label="Heti limit" bucket={usage?.week} scoped={usage?.scoped} /></div>
          <div style={{ ['--i' as string]: 3 }}><ShareTile q={quota} /></div>
        </section>
        <section className="a-dash">
          <div className="a-panel a-pad">
            <div className="a-ph"><h3>Kimenet, utolsó 14 nap</h3><span className="a-meta">token / nap</span></div>
            <div className="a-bars">{days.map((d, i) => <div key={d.date} className="bar" title={`${d.date}: ${fmt(d.output)}`}><i className="a-growy" style={{ height: `${(d.output / maxDay) * 100}%`, ['--i' as string]: i }} /><span>{d.date.slice(8)}</span></div>)}</div>
          </div>
          <div className="a-panel a-pad">
            <div className="a-ph"><h3>Most történik</h3><i className={`a-lamp ${calls.length ? 'on' : ''}`} /></div>
            {calls.length === 0 ? <p className="a-sub">Még nem hívott APM-et egy ügynök sem. Ha egy ablak dolgozik, a hívásai itt jelennek meg.</p> : (
              <ul className="a-feed">{calls.map((c, i) => <li key={`${c.at}-${i}`} className={c.fresh ? 'new' : ''} onClick={() => navigate(`/projects/${c.project_id}`)}><b>{c.project ?? `#${c.project_id}`}</b><span>{c.label}{c.detail ? ` · ${c.detail}` : ''}</span><time>{agoText(c.at)}</time></li>)}</ul>
            )}
          </div>
          <div className="a-panel a-pad wide">
            <div className="a-ph"><h3>Projektek fogyasztása</h3>{stats && <span className="a-meta">kimenet-token · 30 nap összesen {fmt(stats.month.output_tokens)}</span>}</div>
            {projs.length === 0 ? <p className="a-sub">Még nincs mért fogyasztás. <button className="a-btn sm" onClick={() => navigate('/projects')}>Projektek</button></p> : (
              <div className="a-hbars a-stagger">{projs.map((p, i) => <div key={p.project_id} className="hb" style={{ ['--i' as string]: i }} onClick={() => navigate(`/projects/${p.project_id}`)} title={`bemenet ${fmt(p.input_tokens)} · cache ${fmt(p.cache_read_tokens)} · ${p.sessions} ablak`}><span className="n">{p.name}</span><span className="t"><i className="a-grow" style={{ width: `${(p.output_tokens / maxOut) * 100}%` }} /></span><span className="v">{fmt(p.output_tokens)}</span></div>)}</div>
            )}
            <p className="a-note">Az ügynök-ablakok átirataiból mérve. A közös keretet a Munkamenet- és Heti limit mutatja.</p>
          </div>
        </section>
      </div>
    </div>
  );
}
